/**
 * Tab Nomenclatoare Externe
 * =========================
 * 
 * Componenta pentru gestionarea nomenclatoarelor HIS externe (eLogin, EasyMedical, etc.)
 * cu funcționalități de vizualizare, editare și mapping.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Grid,
  Autocomplete,
  Tooltip,
  LinearProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Edit as EditIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Check as CheckIcon,
  CheckCircle as ValidatedIcon,
  AutoMode as AutoIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assessment as StatsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  externalNomenclatorsApi,
  NomenclatorInfo,
  ExternalProcedure,
  ExternalProcedureListResponse,
  MappingSuggestion,
  NomenclatorStats,
  RisSegment,
  ProceduresFilter,
} from '../../services/externalNomenclatorsApi';

// ============================================================================
// TYPES
// ============================================================================

type MappingStatusFilter = 'all' | 'mapped' | 'unmapped' | 'validated' | 'auto';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatusChip: React.FC<{ procedure: ExternalProcedure }> = ({ procedure }) => {
  if (!procedure.mapping_id) {
    return (
      <Chip
        icon={<WarningIcon />}
        label="Nemapat"
        size="small"
        color="warning"
        variant="outlined"
      />
    );
  }
  
  if (procedure.mapping_validated) {
    return (
      <Chip
        icon={<ValidatedIcon />}
        label="Validat"
        size="small"
        color="success"
      />
    );
  }
  
  if (procedure.mapping_type === 'auto') {
    return (
      <Chip
        icon={<AutoIcon />}
        label={`Auto ${procedure.confidence_score ? Math.round(procedure.confidence_score * 100) + '%' : ''}`}
        size="small"
        color="info"
        variant="outlined"
      />
    );
  }
  
  return (
    <Chip
      icon={<CheckIcon />}
      label="Manual"
      size="small"
      color="primary"
      variant="outlined"
    />
  );
};

// ============================================================================
// STATS DIALOG
// ============================================================================

interface StatsDialogProps {
  open: boolean;
  onClose: () => void;
  stats: NomenclatorStats | null;
  nomenclatorName: string;
}

const StatsDialog: React.FC<StatsDialogProps> = ({ open, onClose, stats, nomenclatorName }) => {
  if (!stats) return null;
  
  const mappedPercent = stats.total_procedures > 0 
    ? Math.round((stats.mapped_count / stats.total_procedures) * 100) 
    : 0;
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2, 
        fontSize: '1rem', 
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        📊 Statistici: {nomenclatorName}
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
              Total proceduri
            </Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }} color="primary.main">
              {stats.total_procedures}
            </Typography>
          </Box>
          
          <Box sx={{ p: 1.5, bgcolor: 'grey.900', borderRadius: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', mb: 0.5 }} color="text.secondary">
              Progres mapping ({mappedPercent}%)
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={mappedPercent}
              color="success"
              sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.700' }}
            />
            <Stack direction="row" justifyContent="space-between" mt={1}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} color="success.main">
                ✅ Mapate: {stats.mapped_count}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} color="warning.main">
                ⚠️ Nemapate: {stats.unmapped_count}
              </Typography>
            </Stack>
          </Box>
          
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.dark', color: 'success.contrastText' }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
                  {stats.validated_count}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Validate</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'info.dark', color: 'info.contrastText' }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
                  {stats.auto_mapped_count}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Auto-mapate</Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {stats.avg_confidence_score && (
            <Box>
              <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
                Scor mediu încredere
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }} color="success.main">
                {Math.round(stats.avg_confidence_score * 100)}%
              </Typography>
            </Box>
          )}
          
          <Box>
            <Typography sx={{ fontSize: '0.75rem', mb: 1 }} color="text.secondary">
              Per modalitate
            </Typography>
            <Stack spacing={0.75}>
              {Object.entries(stats.by_modality).map(([mod, data]) => (
                <Box key={mod} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label={mod} size="small" color="primary" sx={{ minWidth: 50, height: 22, fontSize: '0.7rem' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={data.total > 0 ? (data.mapped / data.total) * 100 : 0}
                      color="success"
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.700' }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', minWidth: 50, fontWeight: 600 }}>
                    {data.mapped}/{data.total}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ fontSize: '0.8rem' }}>Închide</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// MAPPING DIALOG
// ============================================================================

interface MappingDialogProps {
  open: boolean;
  onClose: () => void;
  procedure: ExternalProcedure | null;
  hisSystem: string;
  onMappingCreated: () => void;
}

const MappingDialog: React.FC<MappingDialogProps> = ({
  open,
  onClose,
  procedure,
  hisSystem,
  onMappingCreated,
}) => {
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>([]);
  const [allSegments, setAllSegments] = useState<RisSegment[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<RisSegment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  
  useEffect(() => {
    if (open && procedure) {
      loadSuggestions();
      loadSegments();
    }
  }, [open, procedure]);
  
  const loadSuggestions = async () => {
    if (!procedure) return;
    setLoading(true);
    try {
      const data = await externalNomenclatorsApi.getMappingSuggestions(
        hisSystem,
        procedure.id,
        10
      );
      setSuggestions(data);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadSegments = async () => {
    if (!procedure) return;
    try {
      const data = await externalNomenclatorsApi.listRisSegments(
        hisSystem,
        procedure.external_modality || undefined
      );
      setAllSegments(data);
    } catch (error) {
      console.error('Error loading segments:', error);
    }
  };
  
  const handleSave = async () => {
    if (!procedure || !selectedSegment) return;
    setSaving(true);
    try {
      await externalNomenclatorsApi.createMapping(
        hisSystem,
        procedure.id,
        selectedSegment.id,
        notes || undefined
      );
      onMappingCreated();
      onClose();
    } catch (error) {
      console.error('Error creating mapping:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSelectSuggestion = (suggestion: MappingSuggestion) => {
    setSelectedSegment({
      id: suggestion.segment_id,
      code: suggestion.segment_code,
      display_name: suggestion.segment_name,
      modality: suggestion.modality,
    });
  };
  
  if (!procedure) return null;

  // Dialog compact styling
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose() }}
      disableEscapeKeyDown
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        🔗 Mapare: {procedure.external_name}
        <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
          Cod: {procedure.external_code} | Modalitate: {procedure.external_modality}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Sugestii */}
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 1 }}>
              Sugestii (sortate după similaritate)
            </Typography>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <Stack spacing={0.75}>
                {suggestions.map((sug) => (
                  <Paper
                    key={sug.segment_id}
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      border: selectedSegment?.id === sug.segment_id ? 2 : 1,
                      borderColor: selectedSegment?.id === sug.segment_id ? 'primary.main' : 'divider',
                      bgcolor: selectedSegment?.id === sug.segment_id ? 'primary.dark' : 'transparent',
                      '&:hover': { bgcolor: selectedSegment?.id === sug.segment_id ? 'primary.dark' : 'action.hover' },
                    }}
                    onClick={() => handleSelectSuggestion(sug)}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                          {sug.segment_name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem' }} color="text.secondary">
                          {sug.segment_code}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${Math.round(sug.similarity_score * 100)}%`}
                        size="small"
                        color={sug.similarity_score >= 0.8 ? 'success' : sug.similarity_score >= 0.6 ? 'warning' : 'default'}
                        sx={{ height: 22, fontSize: '0.72rem' }}
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
          
          <Divider />
          
          {/* Căutare manuală */}
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 1 }}>
              Sau selectează manual
            </Typography>
            <Autocomplete
              options={allSegments}
              getOptionLabel={(option) => `${option.code} - ${option.display_name}`}
              value={selectedSegment}
              onChange={(_, value) => setSelectedSegment(value)}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Caută segment RIS..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Stack>
                    <Typography sx={{ fontSize: '0.82rem' }}>{option.display_name}</Typography>
                    <Typography sx={{ fontSize: '0.72rem' }} color="text.secondary">
                      {option.code} | {option.modality}
                    </Typography>
                  </Stack>
                </li>
              )}
            />
          </Box>
          
          {/* Segment selectat */}
          {selectedSegment && (
            <Alert 
              severity="info"
              sx={{ 
                py: 0.5,
                bgcolor: 'info.dark',
                color: 'info.contrastText',
                '& .MuiAlert-icon': { color: 'info.contrastText' }
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', color: 'inherit' }}>
                <strong>Selectat:</strong> {selectedSegment.display_name}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'inherit' }}>
                Cod: {selectedSegment.code}
              </Typography>
            </Alert>
          )}
          
          {/* Note */}
          <TextField
            label="Note (opțional)"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedSegment || saving}
          size="small"
          startIcon={saving ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
          sx={{ fontSize: '0.8rem' }}
        >
          Salvează Mapare
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// EDIT DIALOG
// ============================================================================

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  procedure: ExternalProcedure | null;
  hisSystem: string;
  onSaved: () => void;
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  onClose,
  procedure,
  hisSystem,
  onSaved,
}) => {
  const [formData, setFormData] = useState({
    external_code: '',
    external_name: '',
    external_modality: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (procedure) {
      setFormData({
        external_code: procedure.external_code,
        external_name: procedure.external_name,
        external_modality: procedure.external_modality || '',
        is_active: procedure.is_active,
      });
    }
  }, [procedure]);
  
  const handleSave = async () => {
    if (!procedure) return;
    setSaving(true);
    try {
      await externalNomenclatorsApi.updateProcedure(hisSystem, procedure.id, formData);
      onSaved();
      onClose();
    } catch (error) {
      console.error('Error updating procedure:', error);
    } finally {
      setSaving(false);
    }
  };
  
  if (!procedure) return null;
  
  const MODALITIES = ['CT', 'MR', 'DX', 'US', 'MG', 'NM', 'PT', 'DEXA'];
  
  // Dialog compact styling
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
    '& .MuiMenuItem-root': { fontSize: '0.8rem' },
  };

  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose() }}
      disableEscapeKeyDown
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        ✏️ Editare Procedură
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField
            label="Cod"
            value={formData.external_code}
            onChange={(e) => setFormData({ ...formData, external_code: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Denumire"
            value={formData.external_name}
            onChange={(e) => setFormData({ ...formData, external_name: e.target.value })}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Modalitate</InputLabel>
            <Select
              value={formData.external_modality}
              label="Modalitate"
              onChange={(e) => setFormData({ ...formData, external_modality: e.target.value })}
            >
              {MODALITIES.map((mod) => (
                <MenuItem key={mod} value={mod}>{mod}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.is_active ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
            >
              <MenuItem value="active">Activ</MenuItem>
              <MenuItem value="inactive">Inactiv</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="small"
          startIcon={saving ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
          sx={{ fontSize: '0.8rem' }}
        >
          Salvează
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ExternalNomenclatorsTab: React.FC = () => {
  // State
  const [nomenclators, setNomenclators] = useState<NomenclatorInfo[]>([]);
  const [selectedNomenclator, setSelectedNomenclator] = useState<string>('');
  const [procedures, setProcedures] = useState<ExternalProcedure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination & Filters
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalityFilter, setModalityFilter] = useState<string>('');
  const [mappingStatusFilter, setMappingStatusFilter] = useState<MappingStatusFilter>('all');
  
  // Dialogs
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [stats, setStats] = useState<NomenclatorStats | null>(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState<ExternalProcedure | null>(null);
  
  // Load nomenclators on mount
  useEffect(() => {
    loadNomenclators();
  }, []);
  
  // Load procedures when filters change
  useEffect(() => {
    if (selectedNomenclator) {
      loadProcedures();
    }
  }, [selectedNomenclator, page, pageSize, searchTerm, modalityFilter, mappingStatusFilter]);
  
  const loadNomenclators = async () => {
    try {
      const data = await externalNomenclatorsApi.listNomenclators();
      setNomenclators(data);
      if (data.length > 0 && !selectedNomenclator) {
        setSelectedNomenclator(data[0].his_system);
      }
    } catch (err) {
      console.error('Error loading nomenclators:', err);
      setError('Nu am putut încărca nomenclatoarele');
    }
  };
  
  const loadProcedures = async () => {
    if (!selectedNomenclator) return;
    setLoading(true);
    try {
      const response = await externalNomenclatorsApi.listProcedures(selectedNomenclator, {
        page: page + 1,
        page_size: pageSize,
        search: searchTerm || undefined,
        modality: modalityFilter || undefined,
        mapping_status: mappingStatusFilter,
      });
      setProcedures(response.items);
      setTotal(response.total);
    } catch (err) {
      console.error('Error loading procedures:', err);
      setError('Nu am putut încărca procedurile');
    } finally {
      setLoading(false);
    }
  };
  
  const loadStats = async () => {
    if (!selectedNomenclator) return;
    try {
      const data = await externalNomenclatorsApi.getNomenclatorStats(selectedNomenclator);
      setStats(data);
      setStatsDialogOpen(true);
    } catch (err) {
      console.error('Error loading stats:', err);
      setError('Nu am putut încărca statisticile');
    }
  };
  
  const handleValidate = async (procedure: ExternalProcedure) => {
    try {
      await externalNomenclatorsApi.validateMapping(selectedNomenclator, procedure.id);
      setSuccess('Mapping validat cu succes');
      loadProcedures();
    } catch (err) {
      console.error('Error validating:', err);
      setError('Eroare la validare');
    }
  };
  
  const handleDeleteMapping = async (procedure: ExternalProcedure) => {
    try {
      await externalNomenclatorsApi.deleteMapping(selectedNomenclator, procedure.id);
      setSuccess('Mapping șters cu succes');
      loadProcedures();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError('Eroare la ștergere');
    }
  };
  
  const handleOpenMapping = (procedure: ExternalProcedure) => {
    setSelectedProcedure(procedure);
    setMappingDialogOpen(true);
  };
  
  const handleOpenEdit = (procedure: ExternalProcedure) => {
    setSelectedProcedure(procedure);
    setEditDialogOpen(true);
  };
  
  const currentNomenclator = nomenclators.find(n => n.his_system === selectedNomenclator);
  const MODALITIES = ['CT', 'MR', 'DX', 'US', 'MG', 'NM', 'PT', 'DEXA'];
  
  // Compact input styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem', height: 32 },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
    '& .MuiMenuItem-root': { fontSize: '0.8rem' }
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header - Compact */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180, ...inputSx }}>
            <InputLabel>Nomenclator</InputLabel>
            <Select
              value={selectedNomenclator}
              label="Nomenclator"
              onChange={(e) => {
                setSelectedNomenclator(e.target.value);
                setPage(0);
              }}
            >
              {nomenclators.map((nom) => (
                <MenuItem key={nom.his_system} value={nom.his_system}>
                  {nom.his_system_name || nom.his_system}
                  <Chip label={`${nom.mapped_count}/${nom.total_procedures}`} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.68rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Caută..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 180, ...inputSx }}
          />

          <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
            <InputLabel>Modalitate</InputLabel>
            <Select
              value={modalityFilter}
              label="Modalitate"
              onChange={(e) => {
                setModalityFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Toate</MenuItem>
              {MODALITIES.map((mod) => (
                <MenuItem key={mod} value={mod}>{mod}</MenuItem>
              ))}
            </Select>
          </FormControl>
            
          <ToggleButtonGroup
            value={mappingStatusFilter}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setMappingStatusFilter(value);
                setPage(0);
              }
            }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { fontSize: '0.72rem', py: 0.5, px: 1, height: 32, textTransform: 'none' } }}
          >
              <ToggleButton value="all">Toate</ToggleButton>
              <ToggleButton value="mapped">Mapate</ToggleButton>
            <ToggleButton value="unmapped">Nemapate</ToggleButton>
            <ToggleButton value="validated">Validate</ToggleButton>
            <ToggleButton value="auto">Auto</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={loadProcedures}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<StatsIcon sx={{ fontSize: 14 }} />}
            onClick={loadStats}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Statistici
          </Button>
            
          {currentNomenclator && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
                Total: {currentNomenclator.total_procedures}
              </Typography>
              <Chip label={`${currentNomenclator.mapped_count} mapate`} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
              <Chip label={`${currentNomenclator.unmapped_count} nemapate`} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Table - Compact */}
      <Paper sx={{
        '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
        '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <TableContainer>
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.75, px: 1 }
              }}>
                <TableCell sx={{ width: 50 }}>Mod</TableCell>
                <TableCell sx={{ width: 100 }}>Cod</TableCell>
                <TableCell>Denumire</TableCell>
                <TableCell sx={{ width: 150 }}>Mapare RIS</TableCell>
                <TableCell sx={{ width: 90 }}>Status</TableCell>
                <TableCell sx={{ width: 100 }} align="center">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {procedures.map((proc) => (
                <TableRow
                  key={proc.id}
                  sx={{
                    opacity: proc.is_active ? 1 : 0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Chip label={proc.external_modality || '?'} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {proc.external_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.78rem' }}>
                      {proc.external_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {proc.ris_segment_code ? (
                      <Tooltip title={proc.ris_segment_name || ''}>
                        <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }} color="primary">
                          {proc.ris_segment_code}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip procedure={proc} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.25} justifyContent="center">
                      {!proc.mapping_id ? (
                        <Tooltip title="Mapează">
                          <IconButton size="small" color="primary" onClick={() => handleOpenMapping(proc)}>
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          {!proc.mapping_validated && (
                            <Tooltip title="Validează">
                              <IconButton size="small" color="success" onClick={() => handleValidate(proc)}>
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Șterge mapare">
                            <IconButton size="small" color="error" onClick={() => handleDeleteMapping(proc)}>
                              <LinkOffIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Editează">
                        <IconButton size="small" onClick={() => handleOpenEdit(proc)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && procedures.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: '0.78rem' }} color="text.secondary">
                      Nu există proceduri pentru filtrele selectate
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Rânduri per pagină:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} din ${count}`}
        />
      </Paper>
      
      {/* Dialogs */}
      <StatsDialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        stats={stats}
        nomenclatorName={currentNomenclator?.his_system_name || selectedNomenclator}
      />
      
      <MappingDialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        procedure={selectedProcedure}
        hisSystem={selectedNomenclator}
        onMappingCreated={() => {
          loadProcedures();
          loadNomenclators();
        }}
      />
      
      <EditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        procedure={selectedProcedure}
        hisSystem={selectedNomenclator}
        onSaved={loadProcedures}
      />
      
      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExternalNomenclatorsTab;

